import React, { useState } from 'react';
import { ArrowRight, CheckCircle2, Lock, Mail, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';

interface RegisterScreenProps {
  onRegisterSuccess?: (user: { email: string; name: string }) => void;
  onBackToLogin?: () => void;
}

export const RegisterScreen: React.FC<RegisterScreenProps> = ({
  onRegisterSuccess,
  onBackToLogin,
}) => {
  const { signIn, signUp, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  // If user is already authenticated, resume pending invite or go home
  if (user) {
    const pendingToken = sessionStorage.getItem('pending_invite_token');
    if (pendingToken) {
      sessionStorage.removeItem('pending_invite_token');
      return <Navigate to={`/invite?token=${pendingToken}`} replace />;
    }
    return <Navigate to="/centro-operacional" replace />;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      if (isRegisterMode) {
        await signUp(email, password);
        setRegistrationSuccess(true);
      } else {
        await signIn(email, password);
        onRegisterSuccess?.({ email, name: email.split('@')[0] });
      }
    } catch (err: any) {
      setError(err.message ?? 'Erro ao autenticar. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col overflow-hidden"
      style={{
        background: '#060d16',
        backgroundImage:
          'linear-gradient(rgba(30,58,95,0.11) 1px, transparent 1px), linear-gradient(90deg, rgba(30,58,95,0.11) 1px, transparent 1px)',
        backgroundSize: '32px 32px',
      }}
    >
      {/* â”€â”€ Ambient glows â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 70% 50% at 50% -5%, rgba(59,130,246,0.09) 0%, transparent 65%)',
        }}
      />
      <div
        className="pointer-events-none absolute bottom-0 right-0 w-[500px] h-[500px]"
        style={{
          background:
            'radial-gradient(circle, rgba(99,102,241,0.035) 0%, transparent 70%)',
        }}
      />

      {/* â”€â”€ Main content â€” vertically centered, stacked â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div className="relative z-10 flex flex-1 flex-col items-center justify-center gap-7 px-4">

        {/* 1 â–¸ Hero logo */}
        <div className="flex flex-col items-center gap-3 animate-in fade-in slide-in-from-top-4 duration-500">
          <div className="relative flex items-center justify-center">
            <div
              className="pointer-events-none absolute"
              style={{
                width: 80,
                height: 80,
                background: 'radial-gradient(circle, rgba(47,123,255,0.20) 0%, transparent 70%)',
                filter: 'blur(12px)',
              }}
            />
            <img
              src="./assets/logo/workflow-icon.png"
              alt="Workflow Manager"
              className="relative h-[67px] w-auto object-contain"
            />
          </div>

          {/* 2 â–¸ Premium title */}
          <div className="flex flex-col items-center gap-0 text-center">
            <h1
              className="text-[22px] leading-tight text-slate-100"
              style={{ fontWeight: 700, letterSpacing: '0.08em' }}
            >
              WORKFLOW MANAGER
            </h1>
            <p
              className="mt-1 text-[16px] text-slate-400 tracking-widest"
              style={{ fontWeight: 400 }}
            >
              Enterprise Excellence Platform
            </p>
            <div
              className="mt-2.5"
              style={{ width: 297, height: 1, background: 'rgba(47,123,255,0.35)' }}
            />
          </div>
        </div>

        {/* 3 â–¸ Frosted glass form card */}
        <div
          className="relative w-full max-w-[360px] animate-in fade-in zoom-in-95 duration-500 delay-75"
          style={{
            background: '#0B1624',
            border: '1px solid #1E3A5F',
            borderRadius: '14px',
            padding: '36px 32px 28px',
            boxShadow: '0 16px 40px rgba(0,0,0,0.5)',
          }}
        >
          {/* Top accent line */}
          <div
            className="absolute top-0 left-8 right-8 h-px"
            style={{
              background:
                'linear-gradient(90deg, transparent, rgba(59,130,246,0.55), rgba(99,102,241,0.3), transparent)',
            }}
          />

          <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
            {/* Email input */}
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                <Mail size={14} className="text-slate-600" />
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded pl-10 pr-4 text-sm text-slate-200 placeholder:text-slate-500 outline-none transition-all duration-150"
                style={{
                  height: 44,
                  background: 'rgba(4,10,20,0.9)',
                  border: '1px solid #223E63',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#2F7BFF';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(47,123,255,0.08)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#223E63';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                placeholder="Email corporativo"
                required
              />
            </div>

            {/* Password input */}
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                <Lock size={14} className="text-slate-600" />
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded pl-10 pr-10 text-sm text-slate-200 placeholder:text-slate-500 outline-none transition-all duration-150"
                style={{
                  height: 44,
                  background: 'rgba(4,10,20,0.9)',
                  border: '1px solid #223E63',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#2F7BFF';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(47,123,255,0.08)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#223E63';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                placeholder="Senha"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-400 transition-colors duration-150"
              >
                {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>

            {/* Error */}
            {error && (
              <div
                className="rounded px-3 py-2 text-center text-[11px] font-medium text-red-400"
                style={{
                  background: 'rgba(239,68,68,0.07)',
                  border: '1px solid rgba(239,68,68,0.18)',
                }}
              >
                {error}
              </div>
            )}

            {/* Success / Submit */}
            {registrationSuccess ? (
              <div
                className="rounded p-4 text-center"
                style={{
                  background: 'rgba(52,211,153,0.07)',
                  border: '1px solid rgba(52,211,153,0.18)',
                }}
              >
                <CheckCircle2 size={18} className="mx-auto mb-2 text-emerald-400" />
                <p className="mb-1 text-[12px] font-semibold text-emerald-400">
                  Cadastro realizado
                </p>
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  Verifique seu email e confirme o cadastro.
                  <br />
                  <span className="text-slate-400 font-medium">
                    Checar caixa de spam se nao receber.
                  </span>
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setRegistrationSuccess(false);
                    setIsRegisterMode(false);
                    onBackToLogin?.();
                  }}
                  className="mt-3 text-[11px] font-semibold text-emerald-400 hover:underline"
                >
                  Voltar para login
                </button>
              </div>
            ) : (
              <button
                type="submit"
                disabled={isLoading}
                className="flex w-full items-center justify-center gap-2 rounded text-sm tracking-wide text-white transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 mt-1"
                style={{
                  height: 46,
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #2F7BFF 0%, #1E5EFF 100%)',
                  boxShadow: '0 4px 18px rgba(47,123,255,0.30)',
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    (e.currentTarget as HTMLButtonElement).style.filter = 'brightness(1.06)';
                    (e.currentTarget as HTMLButtonElement).style.boxShadow =
                      '0 8px 28px rgba(47,123,255,0.42)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isLoading) {
                    (e.currentTarget as HTMLButtonElement).style.filter = 'brightness(1)';
                    (e.currentTarget as HTMLButtonElement).style.boxShadow =
                      '0 4px 18px rgba(47,123,255,0.30)';
                  }
                }}
              >
                {isLoading ? (
                  'Autenticando...'
                ) : (
                  <>
                    {isRegisterMode ? 'CRIAR CONTA' : 'ACESSAR PLATAFORMA'}
                    <ArrowRight size={14} />
                  </>
                )}
              </button>
            )}
          </form>

          {/* Toggle login/register */}
          {!registrationSuccess && (
            <div className="mt-5 text-center">
              <button
                type="button"
                onClick={() => {
                  setIsRegisterMode((v) => !v);
                  setError('');
                }}
                className="text-[11px] font-medium text-blue-500 hover:text-blue-400 transition-colors hover:underline"
              >
                {isRegisterMode ? 'Ja tem conta? Faca login' : 'Nao tem conta? Cadastre-se'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 4 â–¸ Muted footer */}
      <div className="relative z-10 py-5 text-center flex-shrink-0">
        <span className="text-xs text-slate-700 uppercase tracking-widest font-medium select-none">
          wf-enterprise-edition
        </span>
      </div>
    </div>
  );
};

export default RegisterScreen;
